import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import moment from 'moment';
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import toast from 'react-hot-toast';
import userProgramsService from '../../../services/userProgramsService';
import { STATUS_OPTIONS } from '../constants';

export interface RecommendationLetterItem {
  id: string;
  firstName: string;
  lastName: string;
  subjectDepartment: string;
  email: string;
  status: string;
  targetDate: string;
  link: string;
}

function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  return trimmed.startsWith('https://') || trimmed.startsWith('http://');
}

const createEmptyItem = (): RecommendationLetterItem => ({
  id: `letter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  firstName: '',
  lastName: '',
  subjectDepartment: '',
  email: '',
  status: 'not_started',
  targetDate: '',
  link: '',
});

interface RecommendationLetterFormItemProps {
  item: RecommendationLetterItem;
  onChange: (item: RecommendationLetterItem) => void;
  onDelete: () => void;
}

const RecommendationLetterFormItem: React.FC<RecommendationLetterFormItemProps> = ({
  item,
  onChange,
  onDelete,
}) => {
  const update = useCallback(
    (patch: Partial<RecommendationLetterItem>) => {
      onChange({ ...item, ...patch });
    },
    [item, onChange]
  );

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        backgroundColor: '#fff',
      }}
    >
      {/* Row 1: First Name | Last Name | Subject/Department */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}
          >
            First Name
          </label>
          <TextField
            fullWidth
            size="small"
            value={item.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            placeholder=""
            variant="outlined"
            hiddenLabel
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
            }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}
          >
            Last Name
          </label>
          <TextField
            fullWidth
            size="small"
            value={item.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            placeholder=""
            variant="outlined"
            hiddenLabel
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
            }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}
          >
            Subject/Department
          </label>
          <TextField
            fullWidth
            size="small"
            value={item.subjectDepartment}
            onChange={(e) => update({ subjectDepartment: e.target.value })}
            placeholder=""
            variant="outlined"
            hiddenLabel
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
            }}
          />
        </div>
      </div>

      {/* Row 2: Email (full width) */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            color: '#111827',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '8px',
          }}
        >
          Email
        </label>
        <TextField
          fullWidth
          size="small"
          type="email"
          value={item.email}
          onChange={(e) => update({ email: e.target.value })}
          placeholder=""
          variant="outlined"
          hiddenLabel
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
          }}
        />
      </div>

      {/* Row 3: Link (full width) with View Link button */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            color: '#111827',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '8px',
          }}
        >
          Link
        </label>
        <TextField
          fullWidth
          size="small"
          value={item.link}
          onChange={(e) => update({ link: e.target.value })}
          placeholder="https://..."
          variant="outlined"
          hiddenLabel
          error={!!item.link?.trim() && !isValidUrl(item.link)}
          helperText={
            item.link?.trim() && !isValidUrl(item.link)
              ? 'Link must start with https:// or http://'
              : undefined
          }
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
          }}
        />
        {item.link?.trim() && isValidUrl(item.link) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <a
              href={item.link.trim()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <LinkIcon sx={{ fontSize: '18px' }} /> View Link
            </a>
          </div>
        )}
      </div>

      {/* Row 4: Target Date | Status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
        <div>
          <label
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}
          >
            Target Date
          </label>
          <DatePicker
            value={item.targetDate?.trim() && moment(item.targetDate).isValid() ? moment(item.targetDate) : null}
            onChange={(val) => update({ targetDate: val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : '' })}
            inputFormat="MM/DD/yyyy"
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                placeholder="MM/DD/yyyy"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                }}
              />
            )}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              color: '#111827',
              fontSize: '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}
          >
            Status
          </label>
          <FormControl fullWidth size="small">
            <Select
              value={item.status}
              onChange={(e: SelectChangeEvent<string>) => update({ status: e.target.value })}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
              }}
              variant="outlined"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Row 5: Delete */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button
          startIcon={<DeleteOutlineIcon />}
          onClick={onDelete}
          sx={{
            color: '#dc2626',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

interface RecommendationLettersDropdownProps {
  onClose?: () => void;
  onSubmit?: (items: RecommendationLetterItem[]) => void;
  initialItems?: RecommendationLetterItem[];
  /** Mỗi lần tăng = thêm 1 letter từ nút "Add Letters" bên ngoài */
  addLetterTrigger?: number;
  /** ID từ details.programManager.id — khi có thì Save gọi PATCH /api/programs-manager/:id với recommendationLetters */
  programManagerId?: number;
  /** Master deadline từ Application Form; dùng làm default cho targetDate khi rỗng */
  applicationDeadline?: string;
}

const RecommendationLettersDropdown: React.FC<RecommendationLettersDropdownProps> = ({
  onClose,
  onSubmit,
  initialItems,
  addLetterTrigger = 0,
  programManagerId,
  applicationDeadline = '',
}) => {
  const [items, setItems] = useState<RecommendationLetterItem[]>(() =>
    initialItems?.length
      ? initialItems.map((it) => ({ ...createEmptyItem(), ...it, link: (it as any).link ?? '' }))
      : []
  );
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const prevTriggerRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const programManagerIdRef = useRef(programManagerId);
  programManagerIdRef.current = programManagerId;
  const isFirstRender = useRef(true);

  const debouncedAutoSave = useMemo(
    () =>
      debounce(() => {
        const id = programManagerIdRef.current;
        if (id == null) return;
        const current = itemsRef.current;
        if (current.some((it) => it.targetDate?.trim() && isNaN(new Date(it.targetDate).getTime()))) return;
        if (current.some((it) => it.link?.trim() && !isValidUrl(it.link))) return;
        const recommendationLetters = current.map((it) => ({
          firstName: it.firstName,
          lastName: it.lastName,
          subjectRelationship: it.subjectDepartment,
          email: it.email || undefined,
          status: it.status,
          date: it.targetDate || '',
          link: it.link || undefined,
        }));
        userProgramsService
          .patchProgramsManager(id, { recommendationLetters })
          .then(() => {
            setAutoSaved(true);
            toast.success(current.length === 1 ? '1 recommendation letter saved.' : `${current.length} recommendation letters saved.`);
          })
          .catch(() => {});
      }, 800),
    []
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAutoSaved(false);
    debouncedAutoSave();
    return () => debouncedAutoSave.cancel();
  }, [items, debouncedAutoSave]);

  const createItemWithDeadline = useCallback(() => {
    const item = createEmptyItem();
    if (applicationDeadline?.trim()) item.targetDate = applicationDeadline.trim();
    return item;
  }, [applicationDeadline]);

  useEffect(() => {
    if (addLetterTrigger > prevTriggerRef.current) {
      const count = addLetterTrigger - prevTriggerRef.current;
      setItems((prev) => [...prev, ...Array.from({ length: count }, createItemWithDeadline)]);
      prevTriggerRef.current = addLetterTrigger;
    }
  }, [addLetterTrigger, createItemWithDeadline]);

  const handleChange = useCallback((index: number, updated: RecommendationLetterItem) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const handleDelete = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    const invalidDateIndex = items.findIndex(
      (it) => it.targetDate.trim() && isNaN(new Date(it.targetDate.trim()).getTime())
    );
    if (invalidDateIndex !== -1) {
      toast.error(`Please enter a valid Target Date in item ${invalidDateIndex + 1}.`);
      return;
    }

    const invalidLinkIndex = items.findIndex(
      (it) => it.link?.trim() && !isValidUrl(it.link)
    );
    if (invalidLinkIndex !== -1) {
      toast.error(`Please enter a valid link (must start with https:// or http://) in item ${invalidLinkIndex + 1}.`);
      return;
    }

    const recommendationLetters = items.map((it) => ({
      firstName: it.firstName,
      lastName: it.lastName,
      subjectRelationship: it.subjectDepartment,
      email: it.email || undefined,
      status: it.status,
      date: it.targetDate || '',
      link: it.link || undefined,
    }));

    if (programManagerId != null) {
      setSaving(true);
      try {
        await userProgramsService.patchProgramsManager(programManagerId, {
          recommendationLetters,
        });
        const count = items.length;
        toast.success(
          count === 1
            ? '1 recommendation letter saved.'
            : `${count} recommendation letters saved.`
        );
        onSubmit?.(items);
        // Không gọi onClose — giữ form mở để hiển thị số lượng và data đã lưu
      } catch {
        // Error toast handled by http interceptor
      } finally {
        setSaving(false);
      }
    } else {
      onSubmit?.(items);
      onClose?.();
    }
  }, [items, programManagerId, onSubmit, onClose]);

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginTop: '-12px',
        marginBottom: '12px',
      }}
    >
      {items.length === 0 ? (
        <div style={{ color: '#6b7280', fontSize: '14px', padding: '16px 0', textAlign: 'center' }}>
          Click &quot;+ Add Letters&quot; above to add a recommendation letter.
        </div>
      ) : (
        items.map((item, index) => (
          <RecommendationLetterFormItem
            key={item.id}
            item={item}
            onChange={(updated) => handleChange(index, updated)}
            onDelete={() => handleDelete(index)}
          />
        ))
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
        {programManagerId != null && autoSaved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircleIcon sx={{ fontSize: '18px', color: '#16a34a' }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Auto-saved</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          <Button onClick={onClose} sx={{ color: '#6b7280', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ bgcolor: '#2563eb', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecommendationLettersDropdown;
